"use client";

import { useEffect, useState } from "react";
import { Info, AlertTriangle, CheckCircle2, AlertCircle, X } from "lucide-react";

type Banner = {
  id: string;
  title: string;
  message: string;
  type: "info" | "warning" | "success" | "danger";
};

const STYLES: Record<Banner["type"], { bar: string; icon: string; btn: string; dismiss: string }> = {
  info: {
    bar:     "bg-dashboardBlue text-white",
    icon:    "text-white/70",
    btn:     "text-white/80 hover:text-white hover:bg-dashboardBlue-dark",
    dismiss: "hover:bg-dashboardBlue-dark text-white/70 hover:text-white",
  },
  warning: {
    bar:     "bg-amber-500 text-white",
    icon:    "text-amber-100",
    btn:     "text-amber-100 hover:text-white hover:bg-amber-600",
    dismiss: "hover:bg-amber-600 text-amber-100 hover:text-white",
  },
  success: {
    bar:     "bg-dashboardGreen text-white",
    icon:    "text-white/70",
    btn:     "text-white/80 hover:text-white hover:bg-dashboardGreen-dark",
    dismiss: "hover:bg-dashboardGreen-dark text-white/70 hover:text-white",
  },
  danger: {
    bar:     "bg-red-600 text-white",
    icon:    "text-red-200",
    btn:     "text-red-100 hover:text-white hover:bg-red-700",
    dismiss: "hover:bg-red-700 text-red-200 hover:text-white",
  },
};

const ICONS: Record<Banner["type"], React.ElementType> = {
  info:    Info,
  warning: AlertTriangle,
  success: CheckCircle2,
  danger:  AlertCircle,
};

function isDismissed(id: string): boolean {
  try {
    if (localStorage.getItem(`banner-dismissed-${id}`) === "1") return true;
    if (sessionStorage.getItem(`banner-snoozed-${id}`) === "1") return true;
  } catch {}
  return false;
}

function BannerItem({ banner, onLater, onNever }: { banner: Banner; onLater: () => void; onNever: () => void }) {
  const [closing, setClosing] = useState(false);
  const s = STYLES[banner.type];
  const Icon = ICONS[banner.type];

  function close(cb: () => void) {
    setClosing(true);
    setTimeout(cb, 280);
  }

  return (
    <div
      className={`${s.bar} transition-all duration-280 ease-in-out overflow-hidden ${closing ? "max-h-0 opacity-0 py-0" : "max-h-24 opacity-100"}`}
    >
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2.5">
        <Icon className={`h-4 w-4 shrink-0 ${s.icon}`} />

        <p className="min-w-0 flex-1 text-center text-sm">
          <span className="font-semibold">{banner.title}.</span>{" "}
          <span className="opacity-90">{banner.message}</span>
        </p>

        <div className="flex shrink-0 items-center gap-1 text-xs">
          <button
            onClick={() => close(onLater)}
            className={`rounded px-2.5 py-1 font-medium transition-colors ${s.btn}`}
          >
            Remind me later
          </button>
          <span className="opacity-40 select-none">·</span>
          <button
            onClick={() => close(onNever)}
            className={`rounded px-2.5 py-1 font-medium transition-colors ${s.btn}`}
          >
            Don&apos;t show again
          </button>
          <button
            onClick={() => close(onLater)}
            aria-label="Close"
            className={`ml-1 rounded p-1 transition-colors ${s.dismiss}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PublicBannerBar({ banners }: { banners: Banner[] }) {
  const [visible, setVisible] = useState<Banner[]>([]);

  useEffect(() => {
    setVisible(banners.filter((b) => !isDismissed(b.id)));
  }, [banners]);

  function later(id: string) {
    try { sessionStorage.setItem(`banner-snoozed-${id}`, "1"); } catch {}
    setVisible((prev) => prev.filter((b) => b.id !== id));
  }

  function never(id: string) {
    try { localStorage.setItem(`banner-dismissed-${id}`, "1"); } catch {}
    setVisible((prev) => prev.filter((b) => b.id !== id));
  }

  if (!visible.length) return null;

  return (
    <div>
      {visible.map((b) => (
        <BannerItem
          key={b.id}
          banner={b}
          onLater={() => later(b.id)}
          onNever={() => never(b.id)}
        />
      ))}
    </div>
  );
}
